import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { Calendar as BigCalendar, dateFnsLocalizer } from 'react-big-calendar';
import { format, parse, startOfWeek, getDay } from 'date-fns';
import { fr } from 'date-fns/locale';
import { 
  Calendar, Clock, Plus, Search, Edit2, Trash2, Eye, FileText, 
  Download, List, Grid, Filter, Printer, Bell, X
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useData } from '../../contexts/DataContext';
import { PROGRAMME_ITEM_TYPES } from '../../utils/constants';
import { hasPermission, formatDate, exportProgrammeToPDF } from '../../utils/helpers';
import { LOGO_BASE64 } from '../../utils/logoBase64';
import Button from '../common/Button';
import Modal from '../common/Modal';
import ProgrammeEditor from './ProgrammeEditor';
import PrintableProgramme from './PrintableProgramme';
import { logger, performance, logFirebaseError } from '../../utils/logger';

import 'react-big-calendar/lib/css/react-big-calendar.css';
import '../calendar/CalendarView.css';

// Configuration du localisateur français
const locales = { 'fr': fr };

const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek: () => startOfWeek(new Date(), { locale: fr }),
  getDay,
  locales,
});

// Messages en français pour le calendrier
const messages = {
  allDay: 'Journée',
  previous: 'Précédent',
  next: 'Suivant',
  today: "Aujourd'hui",
  month: 'Mois',
  week: 'Semaine',
  day: 'Jour',
  agenda: 'Agenda',
  date: 'Date',
  time: 'Heure',
  event: 'Événement',
  noEventsInRange: 'Aucun événement dans cette période.',
  showMore: total => `+ ${total} événement(s)`
};

const PROGRAMME_TYPES = [
  'Culte',
  'Réunion de Prière',
  'Étude Biblique',
  'Événement Spécial',
  'Autre'
];

const PROGRAMME_STATUS = ['À venir', 'En cours', 'Terminé', 'Annulé'];

const ProgrammesCalendarView = () => {
  const { currentUser } = useAuth();
  const { data, addProgramme, updateProgramme, deleteProgramme } = useData();
  
  // États pour les vues
  const [viewMode, setViewMode] = useState('calendar');
  const [calendarView, setCalendarView] = useState('month');
  
  // États pour les modals
  const [showEditor, setShowEditor] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [editingProgramme, setEditingProgramme] = useState(null);
  const [selectedProgramme, setSelectedProgramme] = useState(null);
  
  // États pour les filtres
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [logoUrl, setLogoUrl] = useState(localStorage.getItem('churchLogo') || '');

  // 🆕 États pour les notifications automatiques
  const [notifications, setNotifications] = useState([]);
  const [showNotifications, setShowNotifications] = useState(false);

  const canManage = hasPermission(currentUser, 'manage_programmes') || 
                    hasPermission(currentUser, 'edit_all');
  const canView = hasPermission(currentUser, 'view_programmes') || 
                  hasPermission(currentUser, 'view_all');

  // 🆕 Fonction pour déterminer le statut basé sur les dates
  const getStatusFromDates = useCallback((date, time, endDate, endTime) => {
    const now = new Date();
    const startDateTime = new Date(`${date}T${time || '10:00'}`);
    
    let endDateTime;
    if (endDate && endTime) {
      endDateTime = new Date(`${endDate}T${endTime}`);
    } else {
      endDateTime = new Date(startDateTime.getTime() + 2 * 60 * 60 * 1000); // +2h par défaut
    }

    if (now < startDateTime) return 'À venir';
    if (now >= startDateTime && now <= endDateTime) return 'En cours';
    if (now > endDateTime) return 'Terminé';
    return 'À venir';
  }, []);

  // 🆕 Mise à jour automatique des statuts
  const updateProgrammeStatuses = useCallback(async () => {
    if (!data.programmes || !canManage) return;

    logger.info('🔄 Vérification des statuts des programmes...');
    
    let updatedCount = 0;
    
    for (const programme of data.programmes) {
      // Ne pas changer les programmes annulés
      if (programme.status === 'Annulé') continue;
      
      const newStatus = getStatusFromDates(
        programme.date, 
        programme.time, 
        programme.endDate, 
        programme.endTime
      );
      
      // Si le statut a changé (et ce n'est pas juste un programme qui devient "À venir")
      if (programme.status !== newStatus && newStatus !== 'À venir') {
        try {
          await updateProgramme(programme.id, { 
            ...programme, 
            status: newStatus 
          });

          // 🆕 Créer une notification
          const notification = {
            id: `${Date.now()}-${Math.random()}`,
            programmeId: programme.id,
            programmeTitle: programme.title,
            oldStatus: programme.status,
            newStatus: newStatus,
            timestamp: new Date(),
            read: false
          };
          
          setNotifications(prev => [notification, ...prev.slice(0, 9)]); // Max 10 notifications
          updatedCount++;
          
          logger.info(`✅ Statut mis à jour: "${programme.title}" → ${newStatus}`);
        } catch (error) {
          logger.error('❌ Erreur mise à jour statut:', error);
        }
      }
    }

    if (updatedCount > 0) {
      logger.info(`🎉 ${updatedCount} programme(s) mis à jour automatiquement`);
    }
  }, [data.programmes, getStatusFromDates, updateProgramme, canManage]);

  // 🆕 Vérification automatique toutes les minutes
  useEffect(() => {
    if (!canManage) return;

    // Vérification initiale
    updateProgrammeStatuses();

    // Vérification périodique toutes les minutes
    const interval = setInterval(updateProgrammeStatuses, 60000);

    return () => clearInterval(interval);
  }, [updateProgrammeStatuses, canManage]);

  // 🆕 Marquer les notifications comme lues
  useEffect(() => {
    if (notifications.length > 0 && !showNotifications) {
      // Marquer automatiquement comme lues après 10 secondes
      const timer = setTimeout(() => {
        setNotifications(prev => 
          prev.map(n => ({ ...n, read: true }))
        );
      }, 10000);

      return () => clearTimeout(timer);
    }
  }, [notifications, showNotifications]);

  // Filtrer les programmes
  const filteredProgrammes = useMemo(() => {
    if (!data.programmes) return [];
    
    return data.programmes.filter(p => {
      const matchesSearch = p.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           p.type?.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesType = filterType === 'all' || p.type === filterType;
      const matchesStatus = filterStatus === 'all' || p.status === filterStatus;
      return matchesSearch && matchesType && matchesStatus;
    }).sort((a, b) => new Date(b.date) - new Date(a.date));
  }, [data.programmes, searchTerm, filterType, filterStatus]);

  // Statistiques améliorées
  const stats = useMemo(() => ({
    total: data.programmes?.length || 0,
    upcoming: data.programmes?.filter(p => p.status === 'À venir').length || 0,
    inProgress: data.programmes?.filter(p => p.status === 'En cours').length || 0,
    completed: data.programmes?.filter(p => p.status === 'Terminé').length || 0,
    thisMonth: data.programmes?.filter(p => {
      const date = new Date(p.date);
      const now = new Date();
      return date.getMonth() === now.getMonth() && date.getFullYear() === now.getFullYear();
    }).length || 0
  }), [data.programmes]);

  // Convertir programmes en événements pour le calendrier
  const events = useMemo(() => {
    return filteredProgrammes.map(prog => {
      const startDate = new Date(prog.date + 'T' + (prog.time || '10:00'));
      const endDate = prog.endDate && prog.endTime 
        ? new Date(prog.endDate + 'T' + prog.endTime)
        : new Date(startDate.getTime() + 2 * 60 * 60 * 1000);

      return {
        id: prog.id,
        title: prog.title,
        start: startDate,
        end: endDate,
        resource: prog,
        type: prog.type,
        status: prog.status
      };
    });
  }, [filteredProgrammes]);

  // Style des événements selon le type et statut
  const eventStyleGetter = (event) => {
    let backgroundColor = '#3174ad';
    
    switch (event.type) {
      case 'Culte':
        backgroundColor = '#3b82f6';
        break;
      case 'Réunion de Prière':
        backgroundColor = '#8b5cf6';
        break;
      case 'Étude Biblique':
        backgroundColor = '#10b981';
        break;
      case 'Événement Spécial':
        backgroundColor = '#f59e0b';
        break;
      default:
        backgroundColor = '#6b7280';
    }

    if (event.status === 'Annulé') {
      backgroundColor = '#ef4444';
    } else if (event.status === 'En cours') {
      backgroundColor = '#10b981'; // Vert pour en cours
    } else if (event.status === 'Terminé') {
      backgroundColor = '#6b7280'; // Gris pour terminé
    }

    return {
      style: {
        backgroundColor,
        borderRadius: '4px',
        opacity: 0.9,
        color: 'white',
        border: '0px',
        display: 'block'
      }
    };
  };

  // Handlers
  const handleOpenEditor = (programme = null) => {
    setEditingProgramme(programme);
    setShowEditor(true);
  };

  const handleSaveProgramme = (programmeData) => {
    if (editingProgramme) {
      updateProgramme(editingProgramme.id, programmeData);
    } else {
      addProgramme(programmeData);
    }
    setShowEditor(false);
    setEditingProgramme(null);
  };

  const handleSelectEvent = (event) => {
    setSelectedProgramme(event.resource);
    setShowDetails(true);
  };

  const handleSelectSlot = (slotInfo) => {
    if (!canManage) return;
    
    const newProgramme = {
      date: format(slotInfo.start, 'yyyy-MM-dd'),
      time: format(slotInfo.start, 'HH:mm'),
      title: '',
      type: 'Culte',
      status: 'À venir',
      items: []
    };
    handleOpenEditor(newProgramme);
  };

  const handleDelete = (id) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer ce programme ?')) {
      deleteProgramme(id);
      setShowDetails(false);
    }
  };

  // 🆕 Supprimer une notification
  const dismissNotification = (notificationId) => {
    setNotifications(prev => prev.filter(n => n.id !== notificationId));
  };

  // 🆕 Supprimer toutes les notifications
  const clearAllNotifications = () => {
    setNotifications([]);
    setShowNotifications(false);
  };

  // Export iCal
  const exportToICalendar = async () => {
    try {
      const formatDateTime = (date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        const seconds = '00';
        return `${year}${month}${day}T${hours}${minutes}${seconds}`;
      };

      const escapeText = (text) => {
        return text.replace(/\\/g, '\\\\')
                  .replace(/;/g, '\\;')
                  .replace(/,/g, '\\,')
                  .replace(/\n/g, '\\n');
      };

      let icsContent = [
        'BEGIN:VCALENDAR',
        'VERSION:2.0',
        'PRODID:-//VOMM FJKM Hannover//Church Calendar//FR',
        'CALSCALE:GREGORIAN',
        'METHOD:PUBLISH',
        'X-WR-CALNAME:VOMM FJKM Hannover',
        'X-WR-TIMEZONE:Europe/Berlin',
        'X-WR-CALDESC:Calendrier des programmes VOMM FJKM Hannover'
      ];

      events.forEach((event, index) => {
        const uid = `${Date.now()}-${index}@vomm-fjkm-hannover.de`;
        const now = formatDateTime(new Date());
        const start = formatDateTime(event.start);
        const end = formatDateTime(event.end);
        const status = event.status === 'Annulé' ? 'CANCELLED' : 'CONFIRMED';
        const title = escapeText(event.title);
        const description = escapeText(event.resource?.type || '');

        icsContent.push(
          'BEGIN:VEVENT',
          `UID:${uid}`,
          `DTSTAMP:${now}`,
          `DTSTART:${start}`,
          `DTEND:${end}`,
          `SUMMARY:${title}`,
          `DESCRIPTION:${description}`,
          `LOCATION:VOMM FJKM Hannover`,
          `STATUS:${status}`,
          'BEGIN:VALARM',
          'TRIGGER:-PT15M',
          'ACTION:DISPLAY',
          'DESCRIPTION:Rappel - 15 minutes',
          'END:VALARM',
          'END:VEVENT'
        );
      });

      icsContent.push('END:VCALENDAR');

      const icsText = icsContent.join('\r\n');
      const blob = new Blob([icsText], { type: 'text/calendar;charset=utf-8' });
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.download = `vomm-programmes-${format(new Date(), 'yyyy-MM-dd')}.ics`;
      link.click();
      
      logger.info('Export iCal réussi');
    } catch (error) {
      logger.error('Erreur export iCal:', error);
      alert('Erreur lors de l\'export iCalendar.');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'À venir': return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400';
      case 'En cours': return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400';
      case 'Terminé': return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
      case 'Annulé': return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  // Compte des notifications non lues
  const unreadCount = notifications.filter(n => !n.read).length;

  if (!canView) {
    return (
      <div className="text-center py-12">
        <Calendar className="w-16 h-16 text-gray-400 mx-auto mb-4" />
        <p className="text-gray-600 dark:text-gray-400">
          Vous n'avez pas la permission d'accéder aux programmes.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* 🆕 Panneau de notifications flottant */}
      {notifications.length > 0 && !showNotifications && (
        <div className="fixed top-4 right-4 z-50 max-w-md animate-slide-in">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 p-4">
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-2">
                <Bell className="w-5 h-5 text-blue-500" />
                <h3 className="font-semibold text-gray-900 dark:text-white">
                  Notifications ({unreadCount})
                </h3>
              </div>
              <button
                onClick={() => setShowNotifications(false)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {notifications.slice(0, 3).map(notif => (
                <div
                  key={notif.id}
                  className={`p-3 rounded-lg ${
                    notif.read 
                      ? 'bg-gray-50 dark:bg-gray-700/50' 
                      : 'bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800'
                  }`}
                >
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <p className="font-medium text-sm text-gray-900 dark:text-white">
                        {notif.programmeTitle}
                      </p>
                      <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                        Statut: <span className="font-medium">{notif.oldStatus}</span> → <span className="font-medium text-green-600 dark:text-green-400">{notif.newStatus}</span>
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                        {format(notif.timestamp, 'HH:mm')}
                      </p>
                    </div>
                    <button
                      onClick={() => dismissNotification(notif.id)}
                      className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 ml-2"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {notifications.length > 3 && (
              <button
                onClick={() => setShowNotifications(true)}
                className="w-full mt-3 text-sm text-blue-600 dark:text-blue-400 hover:underline"
              >
                Voir toutes les notifications ({notifications.length})
              </button>
            )}
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="flex items-center gap-3">
          <Calendar className="w-8 h-8 text-blue-600 dark:text-blue-400" />
          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
              Programmes de l'Église
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Gestion du calendrier des événements
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {/* 🆕 Bouton Notifications */}
          {canManage && notifications.length > 0 && (
            <button
              onClick={() => setShowNotifications(!showNotifications)}
              className="relative p-2 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            >
              <Bell className="w-5 h-5" />
              {unreadCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
                  {unreadCount}
                </span>
              )}
            </button>
          )}

          {canManage && (
            <Button
              onClick={() => handleOpenEditor()}
              variant="primary"
              icon={Plus}
            >
              Nouveau Programme
            </Button>
          )}
        </div>
      </div>

      {/* 🆕 Statistiques améliorées */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 hover:shadow-md transition-all duration-300 hover:scale-105">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Total</p>
              <p className="text-3xl font-bold text-gray-900 dark:text-white mt-2">
                {stats.total}
              </p>
            </div>
            <Calendar className="w-12 h-12 text-gray-400" />
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 hover:shadow-md transition-all duration-300 hover:scale-105">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">À venir</p>
              <p className="text-3xl font-bold text-blue-600 dark:text-blue-400 mt-2">
                {stats.upcoming}
              </p>
            </div>
            <Clock className="w-12 h-12 text-blue-400" />
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 hover:shadow-md transition-all duration-300 hover:scale-105">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">En cours</p>
              <p className="text-3xl font-bold text-green-600 dark:text-green-400 mt-2">
                {stats.inProgress}
              </p>
            </div>
            <div className="relative">
              <div className="w-12 h-12 rounded-full bg-green-100 dark:bg-green-900/20 flex items-center justify-center">
                <div className="w-6 h-6 rounded-full bg-green-500 animate-pulse"></div>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 hover:shadow-md transition-all duration-300 hover:scale-105">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Terminés</p>
              <p className="text-3xl font-bold text-gray-600 dark:text-gray-400 mt-2">
                {stats.completed}
              </p>
            </div>
            <div className="w-12 h-12 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center text-2xl">
              ✓
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 hover:shadow-md transition-all duration-300 hover:scale-105">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Ce mois</p>
              <p className="text-3xl font-bold text-purple-600 dark:text-purple-400 mt-2">
                {stats.thisMonth}
              </p>
            </div>
            <div className="text-4xl">📅</div>
          </div>
        </div>
      </div>

      {/* Toolbar - Reste du code inchangé... */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-4">
        <div className="flex flex-col md:flex-row gap-4">
          {/* Search */}
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Rechercher un programme..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
              />
            </div>
          </div>

          {/* Filters */}
          <div className="flex gap-2">
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
            >
              <option value="all">Tous les types</option>
              {PROGRAMME_TYPES.map(type => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>

            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
            >
              <option value="all">Tous les statuts</option>
              {PROGRAMME_STATUS.map(status => (
                <option key={status} value={status}>{status}</option>
              ))}
            </select>
          </div>

          {/* View Toggle */}
          <div className="flex gap-2">
            <button
              onClick={() => setViewMode('calendar')}
              className={`p-2 rounded-lg transition-colors ${
                viewMode === 'calendar'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600'
              }`}
            >
              <Grid className="w-5 h-5" />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`p-2 rounded-lg transition-colors ${
                viewMode === 'list'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600'
              }`}
            >
              <List className="w-5 h-5" />
            </button>
          </div>

          {/* Export */}
          <button
            onClick={exportToICalendar}
            className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors flex items-center gap-2"
          >
            <Download className="w-5 h-5" />
            Export iCal
          </button>
        </div>
      </div>

      {/* Calendar or List View */}
      {viewMode === 'calendar' ? (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <div style={{ height: '700px' }}>
            <BigCalendar
              localizer={localizer}
              events={events}
              startAccessor="start"
              endAccessor="end"
              style={{ height: '100%' }}
              eventPropGetter={eventStyleGetter}
              onSelectEvent={handleSelectEvent}
              onSelectSlot={handleSelectSlot}
              selectable={canManage}
              messages={messages}
              view={calendarView}
              onView={setCalendarView}
              popup
            />
          </div>
        </div>
      ) : (
        /* Liste des programmes - code existant conservé */
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
          <div className="p-6">
            <div className="space-y-4">
              {filteredProgrammes.length === 0 ? (
                <div className="text-center py-12">
                  <Calendar className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600 dark:text-gray-400">
                    Aucun programme trouvé
                  </p>
                </div>
              ) : (
                filteredProgrammes.map(programme => (
                  <div
                    key={programme.id}
                    className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer"
                    onClick={() => {
                      setSelectedProgramme(programme);
                      setShowDetails(true);
                    }}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3">
                          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                            {programme.title}
                          </h3>
                          <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(programme.status)}`}>
                            {programme.status}
                          </span>
                        </div>
                        <div className="flex items-center gap-4 mt-2 text-sm text-gray-600 dark:text-gray-400">
                          <span className="flex items-center gap-1">
                            <Calendar className="w-4 h-4" />
                            {formatDate(programme.date)}
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock className="w-4 h-4" />
                            {programme.time || '10:00'}
                          </span>
                          <span className="px-2 py-1 bg-gray-100 dark:bg-gray-700 rounded text-xs">
                            {programme.type}
                          </span>
                        </div>
                      </div>
                      {canManage && (
                        <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
                          <button
                            onClick={() => handleOpenEditor(programme)}
                            className="p-2 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg"
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(programme.id)}
                            className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modal Notifications Complète */}
      {showNotifications && (
        <Modal
          isOpen={showNotifications}
          onClose={() => setShowNotifications(false)}
          title="Notifications"
        >
          <div className="space-y-3">
            {notifications.length === 0 ? (
              <p className="text-center py-8 text-gray-600 dark:text-gray-400">
                Aucune notification
              </p>
            ) : (
              <>
                {notifications.map(notif => (
                  <div
                    key={notif.id}
                    className={`p-4 rounded-lg ${
                      notif.read 
                        ? 'bg-gray-50 dark:bg-gray-700/50' 
                        : 'bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800'
                    }`}
                  >
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <p className="font-medium text-gray-900 dark:text-white">
                          {notif.programmeTitle}
                        </p>
                        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                          Statut: <span className="font-medium">{notif.oldStatus}</span> → 
                          <span className="font-medium text-green-600 dark:text-green-400"> {notif.newStatus}</span>
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-500 mt-2">
                          {format(notif.timestamp, 'dd/MM/yyyy à HH:mm')}
                        </p>
                      </div>
                      <button
                        onClick={() => dismissNotification(notif.id)}
                        className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 ml-2"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}

                <button
                  onClick={clearAllNotifications}
                  className="w-full mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                >
                  Effacer toutes les notifications
                </button>
              </>
            )}
          </div>
        </Modal>
      )}

      {/* Modal ProgrammeEditor */}
      <Modal
        isOpen={showEditor}
        onClose={() => {
          setShowEditor(false);
          setEditingProgramme(null);
        }}
        title={editingProgramme ? 'Modifier le programme' : 'Nouveau programme'}
        size="xl"
      >
        <ProgrammeEditor
          programme={editingProgramme}
          onSave={handleSaveProgramme}
          onCancel={() => {
            setShowEditor(false);
            setEditingProgramme(null);
          }}
        />
      </Modal>

      {/* Modal Détails - Design Original Complet */}
      {showDetails && selectedProgramme && (
        <Modal
          isOpen={showDetails}
          onClose={() => {
            setShowDetails(false);
            setSelectedProgramme(null);
          }}
          title="Détails du programme"
          size="lg"
        >
          <div className="space-y-6">
            {/* En-tête avec titre et badge statut */}
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-gray-800 dark:to-gray-700 p-4 rounded-lg">
              <div className="flex items-start justify-between mb-3">
                <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100">
                  {selectedProgramme.title}
                </h3>
                <span className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(selectedProgramme.status)}`}>
                  {selectedProgramme.status}
                </span>
              </div>
              
              {/* Informations principales */}
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <div className="flex items-center">
                  <Calendar className="w-4 h-4 text-blue-600 dark:text-blue-400 mr-2" />
                  <div>
                    <p className="text-xs text-gray-600 dark:text-gray-400">Date</p>
                    <p className="font-medium text-sm dark:text-gray-100">{formatDate(selectedProgramme.date)}</p>
                  </div>
                </div>
                <div className="flex items-center">
                  <Clock className="w-4 h-4 text-blue-600 dark:text-blue-400 mr-2" />
                  <div>
                    <p className="text-xs text-gray-600 dark:text-gray-400">Heure</p>
                    <p className="font-medium text-sm dark:text-gray-100">{selectedProgramme.time || '10:00'}</p>
                  </div>
                </div>
                <div className="flex items-center">
                  <List className="w-4 h-4 text-blue-600 dark:text-blue-400 mr-2" />
                  <div>
                    <p className="text-xs text-gray-600 dark:text-gray-400">Type</p>
                    <p className="font-medium text-sm dark:text-gray-100">{selectedProgramme.type}</p>
                  </div>
                </div>
              </div>

              {/* Statistiques rapides */}
              <div className="mt-4 pt-4 border-t border-blue-100 dark:border-gray-600">
                <div className="flex gap-6 text-sm">
                  <div>
                    <span className="text-gray-600 dark:text-gray-400">Nombre d'éléments: </span>
                    <span className="font-semibold text-gray-900 dark:text-gray-100">
                      {selectedProgramme.items?.length || 0}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-600 dark:text-gray-400">Durée totale: </span>
                    <span className="font-semibold text-gray-900 dark:text-gray-100">
                      {selectedProgramme.items?.reduce((sum, item) => {
                        return item.durationIndeterminate ? sum : sum + (parseInt(item.duration) || 0);
                      }, 0) || 0} min
                      {selectedProgramme.items?.some(item => item.durationIndeterminate) && '+'}
                    </span>
                  </div>
                </div>
              </div>
              
              {/* Description si elle existe */}
              {selectedProgramme.description && (
                <div className="mt-4 pt-4 border-t border-blue-100 dark:border-gray-600">
                  <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">Description</p>
                  <p className="text-sm text-gray-700 dark:text-gray-300">{selectedProgramme.description}</p>
                </div>
              )}
            </div>

            {/* Déroulement du programme */}
            <div>
              <h4 className="text-md font-medium text-gray-900 dark:text-gray-100 mb-3">
                Déroulement du programme
              </h4>
              <div className="space-y-3">
                {selectedProgramme.items?.length > 0 ? (
                  selectedProgramme.items.map((item, index) => {
                    // Déterminer le titre à afficher
                    let displayTitle = '';
                    let subtitle = '';
                    
                    if (item.type === 'hira' || item.type === 'hira_stk') {
                      // Pour les Fihirana
                      const fihirana = data.fihirana?.find(f => f.id === item.fihiranaId);
                      if (fihirana) {
                        displayTitle = `Fihirana n°${fihirana.number}`;
                        subtitle = fihirana.title;
                      } else {
                        const itemTypeInfo = PROGRAMME_ITEM_TYPES[item.type];
                        displayTitle = itemTypeInfo?.label || item.type || 'Hira';
                      }
                    } else {
                      // Pour les autres types : afficher le label du type
                      const itemTypeInfo = PROGRAMME_ITEM_TYPES[item.type];
                      displayTitle = itemTypeInfo?.label || item.type || 'Élément';
                      
                      // Si un titre personnalisé existe, l'afficher en sous-titre
                      if (item.title) {
                        subtitle = item.title;
                      }
                    }
                    
                    return (
                      <div key={item.id} className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                        <div className="flex items-start">
                          <div className="flex-shrink-0 w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-medium">
                            {index + 1}
                          </div>
                          <div className="ml-3 flex-1">
                            <div className="flex items-center justify-between mb-2">
                              <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                                {displayTitle}
                              </p>
                              {item.durationIndeterminate ? (
                                <span className="text-xs text-gray-500 dark:text-gray-400 italic">
                                  Durée indéterminée
                                </span>
                              ) : item.duration ? (
                                <span className="text-xs bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 px-2 py-1 rounded">
                                  {item.duration} min
                                </span>
                              ) : null}
                            </div>
                            
                            {subtitle && (
                              <p className="text-sm text-gray-700 dark:text-gray-300 mb-2">
                                {subtitle}
                              </p>
                            )}
                            
                            <div className="space-y-1 text-xs text-gray-600 dark:text-gray-400">
                              {item.leader && (
                                <p>
                                  <span className="font-medium text-gray-700 dark:text-gray-300">Responsable:</span> {item.leader}
                                </p>
                              )}
                              
                              {item.responsible && (
                                <p>
                                  <span className="font-medium text-gray-700 dark:text-gray-300">Responsable:</span> {item.responsible}
                                </p>
                              )}
                              
                              {(item.type === 'hira' || item.type === 'hira_stk') && item.andininy && (
                                <p>
                                  <span className="font-medium text-gray-700 dark:text-gray-300">Andininy:</span> {item.andininy}
                                </p>
                              )}
                              
                              {item.fihiranaNumber && (
                                <p>
                                  <span className="font-medium text-gray-700 dark:text-gray-300">Fihirana:</span> {item.fihiranaNumber}
                                </p>
                              )}
                              
                              {item.notes && (
                                <p className="mt-2 text-xs italic text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-600 p-2 rounded">
                                  <span className="font-medium not-italic text-gray-700 dark:text-gray-300">Note:</span> {item.notes}
                                </p>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <p className="text-sm text-gray-500 dark:text-gray-400 italic">
                    Aucun élément dans ce programme
                  </p>
                )}
              </div>
            </div>

            {/* Boutons d'action */}
            <div className="flex justify-end gap-3 pt-4 border-t dark:border-gray-700">
              <Button
                variant="outline"
                onClick={() => setShowDetails(false)}
              >
                Fermer
              </Button>
              <Button
                variant="outline"
                onClick={() => exportProgrammeToPDF(selectedProgramme, LOGO_BASE64, data.fihirana)}
                icon={FileText}
                title="Exporter en PDF professionnel"
              >
                PDF
              </Button>

              {canManage && (
                <>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setShowDetails(false);
                      handleOpenEditor(selectedProgramme);
                    }}
                    icon={Edit2}
                  >
                    Modifier
                  </Button>
                  <Button
                    variant="danger"
                    onClick={() => handleDelete(selectedProgramme.id)}
                    icon={Trash2}
                  >
                    Supprimer
                  </Button>
                </>
              )}
            </div>
          </div>
        </Modal>
      )}

      {/* CSS pour les animations */}
      <style dangerouslySetInnerHTML={{__html: `
        @keyframes slide-in {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
        .animate-slide-in {
          animation: slide-in 0.3s ease-out;
        }
      `}} />
    </div>
  );
};

export default ProgrammesCalendarView;
